/**
 * ProjectMedia — add/view photos, videos, and reference links for a project.
 *
 * Mobile-first: the "Add photo/video" button opens the phone camera (via a file
 * input with capture) or the gallery; a link field takes a Home Depot/Pinterest
 * URL. Files upload straight to S3 via a presigned URL with a progress bar.
 * Used on the customer project view and the staff (OBO) project screen.
 * See .kiro/specs/project-media.md.
 */
import { useEffect, useRef, useState } from 'react';
import {
  listProjectMedia,
  uploadProjectMedia,
  addProjectLink,
  deleteProjectMedia,
  type ProjectMediaItem,
} from '../lib/api';

export function ProjectMedia({ projectId }: { projectId: string }): JSX.Element {
  const [items, setItems] = useState<ProjectMediaItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ name: string; pct: number } | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    listProjectMedia(projectId)
      .then((m) => active && setItems(m))
      .catch(() => active && setError('We couldn\u2019t load the photos just now.'));
    return () => {
      active = false;
    };
  }, [projectId]);

  async function refresh(): Promise<void> {
    setItems(await listProjectMedia(projectId));
  }

  async function onFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    setError(null);
    try {
      for (const file of Array.from(files)) {
        setUploading({ name: file.name, pct: 0 });
        await uploadProjectMedia(projectId, file, {
          onProgress: (f) => setUploading({ name: file.name, pct: Math.round(f * 100) }),
        });
      }
      await refresh();
    } catch {
      setError('That upload didn\u2019t go through. Try again.');
    } finally {
      setUploading(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function onAddLink(): Promise<void> {
    const url = linkUrl.trim();
    if (!url) return;
    if (!url.startsWith('https://')) {
      setError('Links must start with https://');
      return;
    }
    setAddingLink(true);
    setError(null);
    try {
      await addProjectLink(projectId, url, linkLabel.trim() || undefined);
      setLinkUrl('');
      setLinkLabel('');
      await refresh();
    } catch {
      setError('We couldn\u2019t add that link. Check it and try again.');
    } finally {
      setAddingLink(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setError(null);
    try {
      await deleteProjectMedia(projectId, id);
      await refresh();
    } catch {
      setError('We couldn\u2019t remove that. Try again.');
    }
  }

  return (
    <div className="pb-surface" style={{ marginTop: 'var(--pb-3)' }}>
      <div className="pb-option__label" style={{ marginBottom: 'var(--pb-1)' }}>Photos, videos &amp; links</div>
      <p className="estimate__hint" style={{ marginBottom: 'var(--pb-2)' }}>
        Add pictures or a quick video of the space, and links to anything you like
        (a Home Depot list, a Pinterest board). The more detail, the better the estimate.
      </p>

      {/* Add photo/video. No `capture` attribute, so mobile offers the FULL
          choice: take a new photo/video OR pick existing ones from the gallery.
          (capture="environment" forced the camera and hid the gallery.) */}
      <input
        ref={fileInput}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => void onFiles(e.target.files)}
      />
      <div className="q-actions" style={{ flexWrap: 'wrap', marginBottom: 'var(--pb-2)' }}>
        <button
          type="button"
          className="pb-btn"
          onClick={() => fileInput.current?.click()}
          disabled={uploading != null}
        >
          {uploading ? `Uploading ${uploading.pct}%\u2026` : 'Add or take photo/video'}
        </button>
      </div>

      {uploading ? (
        <div className="progress__track" style={{ marginBottom: 'var(--pb-2)' }}>
          <div className="progress__fill" style={{ width: `${uploading.pct}%` }} />
        </div>
      ) : null}

      {/* Add a reference link. */}
      <div className="subfield" style={{ marginBottom: 'var(--pb-2)' }}>
        <input
          className="pb-field"
          inputMode="url"
          placeholder="Paste a link (https://\u2026)"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          style={{ marginBottom: 'var(--pb-1)' }}
        />
        <div className="q-actions" style={{ gap: 'var(--pb-1)' }}>
          <input
            className="pb-field"
            placeholder="Label (optional)"
            value={linkLabel}
            onChange={(e) => setLinkLabel(e.target.value)}
          />
          <button type="button" className="pb-btn pb-btn--ghost" onClick={() => void onAddLink()} disabled={addingLink || !linkUrl.trim()}>
            {addingLink ? 'Adding\u2026' : 'Add link'}
          </button>
        </div>
      </div>

      {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

      {/* Gallery */}
      {items == null ? (
        <p className="estimate__hint">Loading&hellip;</p>
      ) : items.length === 0 ? (
        <p className="estimate__hint">Nothing added yet.</p>
      ) : (
        <div className="media-grid">
          {items.map((m) => (
            <div key={m.id} className="media-tile">
              {m.kind === 'image' && m.url ? (
                <a href={m.url} target="_blank" rel="noreferrer">
                  <img src={m.url} alt={m.label ?? 'Project photo'} className="media-tile__img" />
                </a>
              ) : m.kind === 'video' && m.url ? (
                <video src={m.url} controls className="media-tile__img" />
              ) : (
                <a href={m.url ?? '#'} target="_blank" rel="noreferrer" className="media-tile__link">
                  🔗 {m.label || m.url}
                </a>
              )}
              <div className="media-tile__row">
                <span className="estimate__hint">{m.label ?? (m.kind === 'link' ? 'Link' : m.kind)}</span>
                <button type="button" className="progress__back" onClick={() => void remove(m.id)}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
